/**
 * size-guide.js — MAMAVEA Size Guide
 * ─────────────────────────────────────────────────────────────────────────────
 * Responsibilities:
 *  1. Open / close modal with spring animation & focus trap
 *  2. Build table headers + rows from SizeGuideData (size-guide-data.liquid)
 *  3. Filter table rows to only show sizes that exist on this product
 *  4. CM / IN unit toggle — instant, no page reload
 *
 * Dependency: window.SizeGuideData — injected by snippets/size-guide-data.liquid
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  /* Canonical display order for apparel sizes */
  const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

  /* ══════════════════════════════════════════════════════════════════════════
     SizeGuideModal — one instance per trigger / overlay pair on the page
  ══════════════════════════════════════════════════════════════════════════ */
  class SizeGuideModal {
    constructor(trigger, overlay) {
      this.trigger  = trigger;
      this.overlay  = overlay;
      this.panel    = overlay.querySelector('.sg-panel');

      /* Data attributes set by Liquid */
      this.guideType      = (trigger.dataset.sgGuideType || 'shirt').toLowerCase();
      this.isOneSize      = trigger.dataset.sgIsOneSize === 'true';
      this.availableSizes = (trigger.dataset.sgAvailableSizes || '')
                              .split('|')
                              .map(s => s.trim().toUpperCase())
                              .filter(Boolean);

      this.currentUnit   = 'cm';
      this._lastFocused  = null;
      this._keyHandler   = null;

      // Debug: inspect what Liquid passed — open browser console to see this
      console.log('[SizeGuide] guide:', this.guideType, '| sizes from variants:', this.availableSizes);

      this._buildUI();
      this._bindEvents();
    }

    /* ── Build ──────────────────────────────────────────────────────────── */

    _buildUI() {
      const data = (window.SizeGuideData || {})[this.guideType];
      if (!data) {
        console.warn('[SizeGuide] No data for guide type:', this.guideType);
        this.trigger.hidden = true;
        this.trigger.style.display = 'none';
        return;
      }
      this._data = data;

      if (this.isOneSize) {
        this._showOneSizeMessage(data.one_size_text);
      } else if (data.is_dual_table) {
        /* Two-piece set — render top + bottom tables */
        const hasVisible = this._buildDualTable();
        if (!hasVisible) {
          this.trigger.hidden = true;
          this.trigger.style.display = 'none';
        }
      } else {
        const hasVisible = this._buildTable(data);
        if (!hasVisible) {
          this.trigger.hidden = true;
          this.trigger.style.display = 'none';
        }
      }
    }

    _showOneSizeMessage(text) {
      const msg = this.overlay.querySelector('.sg-one-size-message');
      if (!msg) return;
      const p = msg.querySelector('.sg-one-size-message__text');
      if (p) p.textContent = text;
      msg.hidden = false;

      this.overlay.querySelectorAll('.sg-table-wrap').forEach(w => { w.hidden = true; });
    }

    /* Render both top and bottom tables for a two-piece set. */
    _buildDualTable() {
      const sgData = window.SizeGuideData || {};
      const topData    = sgData['set_top'];
      const bottomData = sgData['set_bottom'];

      /* Reveal bottom wrap */
      const bottomWrap = this.overlay.querySelector('[data-sg-table="bottom"]');
      if (bottomWrap) bottomWrap.hidden = false;

      /* Show section labels on the top wrap */
      const topLabelEl = this.overlay.querySelector('[data-sg-table="top"] .sg-section-label');
      if (topLabelEl) topLabelEl.hidden = false;

      let hasVisible = false;
      if (topData)    hasVisible = this._buildTableInWrap(topData,    '[data-sg-table="top"]')    || hasVisible;
      if (bottomData) hasVisible = this._buildTableInWrap(bottomData, '[data-sg-table="bottom"]') || hasVisible;
      return hasVisible;
    }

    /* Build a table inside a specific wrap selector. */
    _buildTableInWrap(data, wrapSelector) {
      const wrap  = this.overlay.querySelector(wrapSelector);
      if (!wrap) return false;
      const thead = wrap.querySelector('.sg-table__head .sg-table__row--head');
      const tbody = wrap.querySelector('.sg-table__body');
      const table = wrap.querySelector('.sg-table');
      if (!thead || !tbody || !table) return false;

      table.setAttribute('data-sg-unit', this.currentUnit);

      const colsCm = data.unit_labels.cm;
      const colsIn = data.unit_labels.in;

      thead.innerHTML = colsCm.map((_, i) => `
        <th scope="col">
          <span class="sg-val-cm">${this._esc(colsCm[i])}</span>
          <span class="sg-val-in">${this._esc(colsIn[i])}</span>
        </th>
      `).join('');

      const rows   = data.rows;
      const sorted = this._sortKeys(Object.keys(rows));
      let hasVisible = false;

      tbody.innerHTML = sorted.map(key => {
        const rowCm   = rows[key].cm;
        const rowIn   = rows[key].in;
        const isAvail = this._isSizeAvailable(key);
        if (isAvail) hasVisible = true;

        const cells = rowCm.map((_, i) => `
          <td>
            <span class="sg-val-cm">${this._esc(String(rowCm[i]))}</span>
            <span class="sg-val-in">${this._esc(String(rowIn[i]))}</span>
          </td>
        `).join('');

        return `<tr class="sg-table__row" data-sg-size="${this._esc(key)}" ${isAvail ? '' : 'data-sg-hidden'}>${cells}</tr>`;
      }).join('');

      const empty = wrap.querySelector('.sg-table__empty');
      if (empty) empty.hidden = hasVisible;

      return hasVisible;
    }

    _buildTable(data) {
      return this._buildTableInWrap(data, '[data-sg-table="top"]');
    }

    /* ── Events ─────────────────────────────────────────────────────────── */

    _bindEvents() {
      /* Open */
      this.trigger.addEventListener('click', () => this.open());

      /* Close — backdrop + close buttons */
      this.overlay.addEventListener('click', e => {
        if (e.target.closest('[data-sg-close]')) this.close();
      });

      /* Unit tabs */
      this.overlay.querySelectorAll('.sg-unit-tab').forEach(tab => {
        tab.addEventListener('click', () => this._setUnit(tab.dataset.sgUnit));
      });
    }

    /* ── Open / Close ───────────────────────────────────────────────────── */

    open() {
      this._lastFocused = document.activeElement;
      this.overlay.hidden = false;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.overlay.classList.add('is-open');
          document.body.classList.add('sg-locked');
          this._trapFocus();
          const first = this._focusable()[0];
          if (first) setTimeout(() => first.focus(), 40);
        });
      });
    }

    close() {
      this.overlay.classList.remove('is-open');
      document.body.classList.remove('sg-locked');

      if (this._keyHandler) {
        document.removeEventListener('keydown', this._keyHandler);
        this._keyHandler = null;
      }

      const onEnd = () => {
        this.overlay.hidden = true;
        this.overlay.removeEventListener('transitionend', onEnd);
      };
      this.overlay.addEventListener('transitionend', onEnd);

      if (this._lastFocused) this._lastFocused.focus();
    }

    /* ── Focus Trap ─────────────────────────────────────────────────────── */

    _focusable() {
      return Array.from(
        this.panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.closest('[hidden]'));
    }

    _trapFocus() {
      this._keyHandler = e => {
        if (e.key === 'Escape') { this.close(); return; }
        if (e.key !== 'Tab') return;
        const els   = this._focusable();
        if (!els.length) { e.preventDefault(); return; }
        const first = els[0];
        const last  = els[els.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      };
      document.addEventListener('keydown', this._keyHandler);
    }

    /* ── Unit Toggle ────────────────────────────────────────────────────── */

    _setUnit(unit) {
      if (unit === this.currentUnit) return;
      this.currentUnit = unit;

      /* Sync all tables (handles both single and dual-table guides) */
      this.overlay.querySelectorAll('.sg-table').forEach(table => {
        table.setAttribute('data-sg-unit', unit);
      });

      this.overlay.querySelectorAll('.sg-unit-tab').forEach(tab => {
        const active = tab.dataset.sgUnit === unit;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
      });
    }

    /* ── Helpers ────────────────────────────────────────────────────────── */

    _isSizeAvailable(key) {
      // No filter list → show every row in the data
      if (!this.availableSizes.length) return true;

      const k = key.toUpperCase().trim();

      // Long-form name → abbreviated key map
      const LONG_FORM = {
        'EXTRA SMALL':       'XS',
        'EXTRA-SMALL':       'XS',
        'SMALL':             'S',
        'MEDIUM':            'M',
        'LARGE':             'L',
        'EXTRA LARGE':       'XL',
        'EXTRA-LARGE':       'XL',
        'DOUBLE EXTRA LARGE':'2XL',
        '2X-LARGE':          '2XL',
        'XXL':               '2XL',
        '2X':                '2XL',
        'TRIPLE EXTRA LARGE':'3XL',
        '3X-LARGE':          '3XL',
        'XXXL':              '3XL',
        '3X':                '3XL',
        'XXXXL':             '4XL',
        '4X-LARGE':          '4XL',
        '4X':                '4XL',
        'XXXXXL':            '5XL',
        '5X-LARGE':          '5XL',
        '5X':                '5XL',
      };

      return this.availableSizes.some(s => {
        const sv = s.toUpperCase().trim();

        // 1. Exact match
        if (sv === k) return true;

        // 2. Normalise long-form on the variant side → compare to data key
        const normS = LONG_FORM[sv] || sv;
        if (normS === k) return true;

        // 3. Normalise long-form on the data key side → compare to variant value
        const normK = LONG_FORM[k] || k;
        if (normK === sv) return true;
        if (normK === normS) return true;

        // 4. Numeric comparison for shoe sizes (e.g. "40" === "40.0")
        if (!isNaN(k) && !isNaN(sv) && parseFloat(k) === parseFloat(sv)) return true;

        return false;
      });
    }

    _sortKeys(keys) {
      const apparel = keys
        .filter(k => SIZE_ORDER.includes(k.toUpperCase()))
        .sort((a, b) => SIZE_ORDER.indexOf(a.toUpperCase()) - SIZE_ORDER.indexOf(b.toUpperCase()));
      const numeric = keys
        .filter(k => !isNaN(k))
        .sort((a, b) => parseFloat(a) - parseFloat(b));
      const other   = keys.filter(k => !SIZE_ORDER.includes(k.toUpperCase()) && isNaN(k));
      return [...apparel, ...numeric, ...other];
    }

    _esc(str) {
      return String(str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
  }

  /* ══════════════════════════════════════════════════════════════════════════
     Init — pair every trigger with its overlay
  ══════════════════════════════════════════════════════════════════════════ */

  function init() {
    document.querySelectorAll('.sg-trigger').forEach(trigger => {
      const overlayId = trigger.getAttribute('aria-controls');
      if (!overlayId) return;
      const overlay = document.getElementById(overlayId);
      if (!overlay || overlay.dataset.sgInit) return;
      overlay.dataset.sgInit = '1';
      new SizeGuideModal(trigger, overlay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Support Shopify theme editor live reloads */
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);

})();
